import React from "react";
import _ from "underscore";
import { BookOpen, CalendarClock, ChevronDown, FileText, MapPin, MessageSquare, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Timeago from "../../../../timeago";
import TutorResultCallButton from "./call_button";
import TutorResultSubjects from "./subjects/index";
import TutorResultTutorBiography from "./tutor_details/show";
import TutorAvailability from "./availability/show";
import MeetingAvailability from "./availability/meeting_availability";
import TutorMeetings from "./availability/tutor_meetings";
import TutorResultWarnings from "./tutor_warnings/index";
import TutorSmser from "../../../../tutor_smser";
import Modal from "../../../../modal";
import EmailButton from "../../../../emailer/email_button";
import WhatsappButton from "../../../../whatsapper/whatsapp_button";
import TutorProposer from "../../../../tutor_proposer/show";
import TutorAcceptor from "../../../../tutor_acceptor/show";
import TutorRejector from "../../../../tutor_rejector/show";
import LocalTime from "../../../../local_time";
import store from "../../../../../state/store";
import CountryFlag from "@/components/CountryFlag";

class TutorResult extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      tab: "availability",
    };
  }

  onTabClick = (value) => {
    this.setState({ tab: value });
  };

  onToggleView = () => {
    this.props.onToggleView();
  };

  onOpenEmail = () => {
    store.emit("ui:mailer:open", {
      related_to_type: "Tutor",
      related_to_id: this.props.tutor_result.tutor.id,
    });
  };

  onOpenWhatsapp = () => {
    store.emit("ui:whatsapper:open", {
      related_to_type: "TutoringJob",
      related_to_id: this.props.tutoring_job.id,
      recipient_type: "Tutor",
      recipient_id: this.props.tutor_result.tutor.id,
    });
  };

  onOpenProposer = () => {
    store.emit("ui:tutor_proposer:open", {
      tutor_result: this.props.tutor_result,
      tutoring_job: this.props.tutoring_job,
    });
  };

  onOpenAcceptor = () => {
    store.emit("ui:tutor_acceptor:open", {
      tutor_result: this.props.tutor_result,
      tutoring_job: this.props.tutoring_job,
    });
  };

  onOpenRejector = () => {
    store.emit("ui:tutor_rejector:open", {
      tutor_result: this.props.tutor_result,
      tutoring_job: this.props.tutoring_job,
    });
  };

  onOpenSms = () => {
    store.emit("ui:tutor_smser:open", {
      tutor_id: this.props.tutor_result.tutor.id,
      tutoring_job_id: this.props.tutoring_job.id,
    });
  };

  toggleShortlisted = () => {
    store.emit("tutoring_job:tutor_results:update", {
      tutor_id: this.props.tutor_result.tutor.id,
      tutoring_job_id: this.props.tutoring_job.id,
      shortlisted: !this.props.tutor_result.job_candidate.shortlisted,
    });
  };

  updateJobCandidate = (params) => {
    store.emit(
      "tutoring_job:tutor_results:update",
      _.extend(params, {
        tutor_id: this.props.tutor_result.tutor.id,
        tutoring_job_id: this.props.tutoring_job.id,
      }),
    );
  };

  onTutorApplied = () => {
    const params = { applied: !this.props.tutor_result.job_candidate.applied };
    this.updateJobCandidate(params);
  };

  onTutorDeclined = () => {
    const params = {
      declined: !this.props.tutor_result.job_candidate.declined,
    };
    this.updateJobCandidate(params);
  };

  onNotify = () => {
    store.emit("tutoring_job:tutor_results:notify", {
      tutor_id: this.props.tutor_result.tutor.id,
      tutoring_job_id: this.props.tutoring_job.id,
    });
  };

  onRenotify = () => {
    store.emit("tutoring_job:tutor_results:notify", {
      tutor_id: this.props.tutor_result.tutor.id,
      tutoring_job_id: this.props.tutoring_job.id,
      renotify: true,
    });
  };

  render() {
    const tutor_result = this.props.tutor_result;

    const tabOptions = [
      { value: "availability", name: "Availability" },
      { value: "meetings", name: "Meetings" },
      { value: "subjects", name: "Subjects" },
      { value: "biography", name: "Biography" },
      { value: "warnings", name: "Warnings" },
    ];

    let tutor_proposals = null;

    if (
      this.props.ui.tutor_proposer.viewing_tutor_id === tutor_result.tutor.id
    ) {
      tutor_proposals = _.select(
        this.props.tutor_proposals,
        function (tutor_proposal) {
          return !_.isFinite(tutor_proposal.id);
        },
      );
    }

    return (
      <div className="rounded-b-[10px] border border-t-0 border-primary/40 bg-muted/30 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={tutor_result.tutor.profile_url} target="_blank">
                Profile
              </a>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Tutor State <ChevronDown className="size-3.5"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel className="text-center">Tutor State</DropdownMenuLabel>
                <DropdownMenuItem onSelect={this.onTutorApplied}>
                  Tutor Applied
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={this.onTutorDeclined}>
                  Tutor Declined
                </DropdownMenuItem>
                <DropdownMenuLabel className="text-center">Accept / Reject</DropdownMenuLabel>
                <DropdownMenuItem onSelect={this.onOpenAcceptor}>
                  Accept
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={this.onOpenRejector}>
                  Reject
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {tutor_result.job_candidate.notified ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    title={`Send Job alert to ${tutor_result.tutor.name}`}
                  >
                    Re-notify Tutor <ChevronDown className="size-3.5"/>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel className="text-center">
                    Last Notified:{" "}
                    <Timeago
                      date={tutor_result.job_candidate.last_notified_at}
                    />
                  </DropdownMenuLabel>
                  <DropdownMenuItem onSelect={this.onRenotify}>
                    Re-send Job Alert
                  </DropdownMenuItem>
                  <DropdownMenuItem>Cancel</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    title={`Send Job alert to ${tutor_result.tutor.name}`}
                  >
                    Notify Tutor <ChevronDown className="size-3.5"/>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel className="text-center">
                    Notify Tutor
                  </DropdownMenuLabel>
                  <DropdownMenuItem onSelect={this.onNotify}>
                    Send Job Alert
                  </DropdownMenuItem>
                  <DropdownMenuItem>Cancel</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <span title="Open tutor proposer">
              <Button variant="outline" size="sm" onClick={this.onOpenProposer}>
                Propose
              </Button>
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {tutor_result.actions.can_call ? (
              <TutorResultCallButton
                tutoring_job={this.props.tutoring_job}
                tutor_result={tutor_result}
              />
            ) : null}
            <WhatsappButton onClick={this.onOpenWhatsapp} />
            <Button
              variant="outline"
              size="sm"
              title="Send SMS"
              onClick={this.onOpenSms}
            >
              <MessageSquare className="size-4"/>
            </Button>
            <EmailButton onEmail={this.onOpenEmail} />
            {tutor_result.distance ? (
              <Button
                asChild
                variant="outline"
                size="sm"
                title="View map on Google Maps"
              >
                <a target="_blank" href={tutor_result.directions_link}>
                  <MapPin className="size-3.5"/>
                  {tutor_result.route_distance ? (
                    <span>{tutor_result.route_distance}</span>
                  ) : (
                    <span>{`~${tutor_result.distance}`}</span>
                  )}
                </a>
              </Button>
            ) : null}
          </div>
        </div>
        <div className="mt-2 flex items-center justify-end gap-2">
          {tutor_result.country ? (
            <Badge variant="secondary" className="cursor-pointer">
              <CountryFlag country_code={tutor_result.country.country_code} />{" "}
              {tutor_result.country.name}
            </Badge>
          ) : null}
          {tutor_result.local_time ? (
            <LocalTime local_time={tutor_result.local_time} />
          ) : null}
        </div>
        {this.props.ui.tutor_smser.tutor_id === tutor_result.tutor.id ? (
          <Modal title="Send SMS" size="modal-lg" onClose={() => store.emit('ui:tutor_smser:close')}>
            <TutorSmser value={this.props.ui.tutor_smser.content} />
          </Modal>
        ) : null}
        {this.props.ui.tutor_proposer.viewing_tutor_id ===
        tutor_result.tutor.id ? (
          <TutorProposer
            tutoring_job={this.props.tutoring_job}
            tutor_result={tutor_result}
            tutor_proposals={tutor_proposals}
            ui={this.props.ui}
          />
        ) : null}
        {this.props.ui.tutor_acceptor.viewing_tutor_id ===
        tutor_result.tutor.id ? (
          <TutorAcceptor
            tutor_result={tutor_result}
            tutoring_job={this.props.tutoring_job}
            ui={this.props.ui}
          />
        ) : null}
        {this.props.ui.tutor_rejector.viewing_tutor_id ===
        tutor_result.tutor.id ? (
          <TutorRejector
            tutor_result={tutor_result}
            tutoring_job={this.props.tutoring_job}
            ui={this.props.ui}
          />
        ) : null}
        <Tabs value={this.state.tab} onValueChange={this.onTabClick} className="mt-3 w-fit">
          <TabsList className="max-w-full flex-wrap group-data-horizontal/tabs:h-auto">
            {_.map(tabOptions, (tabOption) => (
              <TabsTrigger key={tabOption.value} value={tabOption.value}>
                {tabOption.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="mt-4 flex flex-col gap-4">
          {this.state.tab === "availability" ? (
            <TutorAvailability
              key={tutor_result.availability?.updated_at}
              availability={tutor_result.availability}
              profile_availability={tutor_result.profile_availability}
              tutor_result={tutor_result}
            />
          ) : null}
          {this.state.tab === "meetings" ? (
            <React.Fragment>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarClock className="size-4"/>
                    Scheduled Meetings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TutorMeetings
                    tutor_id={tutor_result.tutor.id}
                    tutoring_job_id={tutor_result.tutoring_job_id}
                  />
                </CardContent>
              </Card>
              <MeetingAvailability
                meeting_availability={tutor_result.meeting_availability}
                tutor_result={tutor_result}
              />
            </React.Fragment>
          ) : null}
          {this.state.tab === "subjects" ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="size-4"/>
                  Subjects
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Matching Subjects</div>
                <TutorResultSubjects
                  tutor_result={tutor_result}
                  matching={true}
                />
                <div className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">All Subjects</div>
                <TutorResultSubjects
                  tutor_result={tutor_result}
                  matching={false}
                />
              </CardContent>
            </Card>
          ) : null}
          {this.state.tab === "biography" ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="size-4"/>
                  Biography
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TutorResultTutorBiography tutor_result={tutor_result} />
              </CardContent>
            </Card>
          ) : null}
          {this.state.tab === "warnings" ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TriangleAlert className="size-4"/>
                  Warnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tutor_result.tutor_warnings.length > 0 ? (
                  <TutorResultWarnings tutor_result={tutor_result} />
                ) : (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No warnings issued
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    );
  }
}
export default TutorResult;
